import { TelInput } from "@/components/TelInput";
import VerifyAction from "@/components/Verify/Action";
import { useLoginAutoMerge } from "@/utils/gdMerge";
import { useSafeState } from "ahooks";
import { Button, Form } from "antd";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { getMe, register, registerVerify, sendSms } from "../../../services";
import HBIcon from "../../HBIcon";
import HBInput from "../../HBInput";
import HBMessage from "../../HBMessage";
import VerifyInput from "../../Verify/VerifyInput";
import styles from "../index.module.less";
import { LoginType } from "../login";
import Password from "../shared/password";
import rules from "../shared/rules";
import { ThirdLogin } from "../shared/third";
import UserAgreement from "../shared/userAgreement";

export default function Register({
  onOk,
  setType,
  onSwitchLogin,
  isSignup, //是否第三方注册
  onThirdLogin,
  titleStyle,
  ...rest
}) {
  const [step, setStep] = useState(1);
  const formRef = useRef();
  // 表单数据引用
  const dataRef = useRef(null);
  const timeOutRef = useRef(null);
  const verifyRef = useRef(null);

  const switchWechat = useCallback(() => {
    setType(LoginType.WECHAT);
  }, [setType]);

  const switchLogin = useCallback(() => {
    onSwitchLogin(dataRef.current?.tel);
  }, [onSwitchLogin]);

  // 验证码发送倒计时
  const [smsSendTime, setSmsSendTime] = useSafeState(false);

  function countdown(count) {
    timeOutRef.current = setTimeout(() => {
      setSmsSendTime(count - 1);
      if (count > 0) {
        countdown(count - 1);
      } else {
        setSmsSendTime(false);
      }
    }, 1000);
  }

  // 销毁阶段清除定时器
  useEffect(() => {
    return () => clearTimeout(timeOutRef.current);
  }, []);

  const verifyTel = async () => {
    let value;
    try {
      value = await formRef.current.validateFields();
    } catch (error) {
      verifyRef.current.reset();
      return;
    }
    if (!value.validate) {
      HBMessage.error("请先完成滑动验证");
      verifyRef.current.reset();
      return;
    }

    dataRef.current = value;
    const result = await registerVerify({
      ...value,
      tel: value.tel.value,
    });
    if (result.err) {
      verifyRef.current.reset();
      if (result.msg === "该手机号已经绑定账户" && isSignup) {
        setStep(3);
      }
      return;
    }

    setStep(2);
    setSmsSendTime(60);
    countdown(60);
  };

  const sendSmsClick = async () => {
    VerifyAction({
      async onOk(data) {
        await sendSms({
          reason: "signupStep2",
          tel: dataRef.current.tel.value,
          validate: data,
        });

        setSmsSendTime(60);
        countdown(60);
      },
    });
  };

  const autoMerge = useLoginAutoMerge(true);

  const onRegister = async () => {
    const value = await formRef.current.validateFields();

    const result = await register({
      ...value,
      tel: value.tel.value,
      password_confirm: value.password,
    });

    // 错误处理
    if (result.err) {
      if (result.fieldErrors) {
        const err = [];
        for (const key in result.fieldErrors) {
          err.push({
            name: key,
            errors: result.fieldErrors[key],
          });
        }
        formRef.current.setFields(err);
      } else {
        HBMessage.error(result.msg);
      }

      return;
    }

    if (isSignup) {
      // 第三方注册页面
      HBMessage.success("恭喜你，注册成功!");
      onOk();
    } else {
      let user = await getMe();
      const res = await autoMerge(user);
      if (res) {
        // 合并成功，重新获取用户最新信息
        user = await getMe();
      }
      // 成功处理
      HBMessage.success("恭喜你，注册成功!");
      onOk(user);
    }
  };

  // 已经注册过
  if (step === 3) {
    return (
      <div className={styles.register} {...rest}>
        <div
          className={styles.title}
          style={titleStyle || { fontSize: 16, marginBottom: 56 }}
        >
          您输入的手机号 {dataRef.current?.tel?.value}
          已经绑定过其他花瓣账号，您可以登录原账号使用或更换手机号或注销原账号，也可以使用其他手机号绑定当前账号。
          <a
            className="primary-text-btn"
            href="https://support.qq.com/products/30852/faqs/160945"
            target="_blank"
          >
            了解如何登录、变更、注销原账号？
          </a>
        </div>
        <Button
          className={styles.button}
          type="primary"
          onClick={switchLogin}
          style={{
            marginTop: 0,
          }}
        >
          去登录
        </Button>
        <div className={styles.textBtn} onClick={() => setStep(1)}>
          更换手机号
        </div>
      </div>
    );
  }

  return (
    <div className={styles.register} {...rest}>
      {/* <Logo />
        <div className={styles.title}>{title}</div> */}

      {isSignup && (
        <>
          <div className={styles.title} style={titleStyle || {}}>
            绑定手机号
          </div>
          <div
            style={{
              paddingBottom: 12,
              color: "#8A8E91",
              display: "flex",
              alignItems: "center",
            }}
          >
            <HBIcon type="ic_info" style={{ fontSize: 16, marginRight: 4 }} />
            根据相关法律法规要求，需要你填写手机号。
          </div>
        </>
      )}
      <Form className={styles.form} ref={formRef} autoComplete="off">
        <Form.Item
          name="tel"
          className={styles.item}
          rules={rules.tel}
          initialValue={dataRef.current?.tel}
          validateTrigger="onSubmit"
        >
          <TelInput
            placeholder="手机号"
            name="tel"
            disabled={step === 2}
            autoFocus
          />
        </Form.Item>
        {step === 1 && (
          <>
            <Form.Item
              className={classNames([styles.item, styles.last])}
              name="validate"
              data-dialog-title="注册信息弹窗"
              data-gd-expose="dialog_expose"
            >
              <VerifyInput ref={verifyRef} type="register" onOk={verifyTel} />
            </Form.Item>
          </>
        )}
        {step === 2 && (
          <>
            <Form.Item
              className={classNames([styles.item])}
              name="captcha"
              rules={rules.captcha}
            >
              <HBInput
                placeholder="验证码"
                data-dialog-title="注册完善信息弹窗"
                data-gd-expose="dialog_expose"
                addonAfter={
                  <Button
                    className={styles.captcha}
                    disabled={!!smsSendTime}
                    onClick={sendSmsClick}
                  >
                    {smsSendTime ? "重新发送" : "发送验证码"} {smsSendTime}
                  </Button>
                }
              />
            </Form.Item>
            <Form.Item
              className={styles.item}
              name="password"
              rules={rules.password}
            >
              <Password placeholder="设置密码" />
            </Form.Item>
          </>
        )}
      </Form>
      <Button
        className={styles.button}
        type="primary"
        disabled={step === 1}
        onClick={onRegister}
        data-gd-click="button_click"
        data-button-name="注册"
        style={{
          marginBottom: isSignup ? 12 : 0,
        }}
      >
        {isSignup ? "完成" : "注册"}
      </Button>
      {!isSignup && step === 1 && (
        <>
          <div className={styles.other} style={{ marginTop: 36 }}>
            <div
              className={classNames([styles.thirdLoginItem, styles.wechat])}
              data-gd-click="button_click"
              data-button-name="微信登录注册"
              onClick={switchWechat}
              style={{
                marginRight: 12,
              }}
            >
              <HBIcon className={styles.icon} type="ic_wechat1" title="微信" />
            </div>
            <ThirdLogin onOk={onThirdLogin} />
          </div>
        </>
      )}

      {!isSignup && <UserAgreement />}
    </div>
  );
}
